import { Injectable } from '@angular/core';
import { limit, query, where } from '@angular/fire/firestore';
import { CashInCashRegister, CashOutCashRegister } from '@shared';
import { ReplicationSyncStatus } from '@shared/models/replication-sync-status';
import { convertToDate } from '@shared/helpers/convert-to-date';
import { BaseReplicableService } from './base-replicable.service';

type CashTransaction = CashInCashRegister | CashOutCashRegister;

@Injectable({ providedIn: 'root' })
export class CashTransactionService extends BaseReplicableService<CashTransaction> {
  protected readonly entityType = 'cashTransaction';
  protected readonly endpoint = 'CashTransactions';
  protected readonly firestoreCollection = 'cashTransactions';

  protected override readonly replicationEnabled = true;
  protected override readonly defaultSyncStatus = ReplicationSyncStatus.UnSynced;

  /**
   * Fetch cash transactions that still need to be pushed to the backend
   * (Part 2 — Sync). Scoped to the current tenant and user, oldest first so
   * the backend receives them in creation order.
   */
  async getUnsyncedAsync(maxRecords = 500): Promise<CashTransaction[]> {
    return this.queryByStatus(ReplicationSyncStatus.UnSynced, maxRecords);
  }

  /**
   * Transactions the backend rejected on an earlier cycle. They stay local and
   * are retried like `UnSynced` ones — `Failed` records the reason, it does not
   * park the record.
   */
  async getFailedAsync(maxRecords = 500): Promise<CashTransaction[]> {
    return this.queryByStatus(ReplicationSyncStatus.Failed, maxRecords);
  }

  /**
   * Recover stale `Syncing` records left behind by an interrupted push
   * (e.g. a crash between "mark Syncing" and the backend response). They are
   * treated as unsynced so the next cycle retries them.
   */
  async getStaleSyncingAsync(olderThanMs = 5 * 60 * 1000, maxRecords = 500): Promise<CashTransaction[]> {
    const all = await this.queryByStatus(ReplicationSyncStatus.Syncing, maxRecords);
    const threshold = Date.now() - olderThanMs;
    return all.filter(tx => {
      const stamp = tx.metadata?.updatedDate ?? tx.metadata?.createdDate;
      return !stamp || this.toMillis(stamp) < threshold;
    });
  }

  async getByLocalIdAsync(localId: string): Promise<CashTransaction | undefined> {
    return this.getFromFirestore(localId);
  }

  /**
   * Cash transactions recorded on a cash register since it was opened — used
   * by the register-closure calculation (expected cash-in / cash-out).
   *
   * Reads from Firestore (the local source of truth), fully
   * Desktop-independent. Equality-only query so no composite index is
   * required; the open-session window and recorder are filtered client-side
   * (one shift's transactions are a small, bounded set).
   */
  async getForClosureAsync(
    cashRegisterId: string | undefined,
    recordedFrom?: Date | string,
    recordedByEmail?: string,
    maxRecords = 1000
  ): Promise<CashTransaction[]> {
    if (!cashRegisterId) return [];

    const tenantId = this.authService.currentTenant;
    const queryRef = query(
      this.firestoreService.col<CashTransaction>(this.firestoreCollection),
      where('metadata.organizationInfo.id', '==', tenantId),
      where('cashRegisterId', '==', cashRegisterId),
      limit(maxRecords)
    );
    let results = await this.firestoreService.getCollectionAsync<CashTransaction>(queryRef);

    const fromMs = recordedFrom ? this.toMillis(recordedFrom) : 0;
    if (fromMs) {
      results = results.filter(tx => this.toMillis(tx.recordedDate) >= fromMs);
    }
    if (recordedByEmail) {
      results = results.filter(
        tx => tx.recordedBy === recordedByEmail || tx.metadata?.createdBy?.email === recordedByEmail
      );
    }
    return results;
  }

  /**
   * Persist a record recovered from the Desktop into Firestore without
   * re-emitting a replication event (it already exists on the Desktop).
   */
  async restoreToFirestore(entity: CashTransaction): Promise<void> {
    await this.saveToFirestore(entity);
  }

  private async queryByStatus(
    status: ReplicationSyncStatus,
    maxRecords: number
  ): Promise<CashTransaction[]> {
    const tenantId = this.authService.currentTenant;
    const userEmail = this.authService.userProfile?.email;
    // Equality-only query (no orderBy) so Firestore can serve it from
    // single-field indexes without a deployed composite index. Records are
    // sorted by creation order client-side — batches are bounded by `limit`.
    const queryRef = query(
      this.firestoreService.col<CashTransaction>(this.firestoreCollection),
      where('metadata.organizationInfo.id', '==', tenantId),
      where('metadata.createdBy.email', '==', userEmail),
      where('metadata.syncInfo.syncStatus', '==', status),
      limit(maxRecords)
    );
    const results = await this.firestoreService.getCollectionAsync<CashTransaction>(queryRef);
    return results.sort(
      (a, b) => this.toMillis(a.metadata?.createdDate) - this.toMillis(b.metadata?.createdDate)
    );
  }

  /**
   * Normalize a date that may be a Firestore Timestamp, an ISO string, or a
   * Date into epoch milliseconds (0 when absent/invalid).
   */
  private toMillis(value: unknown): number {
    if (!value) return 0;
    const date = convertToDate(value as any);
    const time = date instanceof Date ? date.getTime() : NaN;
    return isNaN(time) ? 0 : time;
  }
}
